import { type ReactNode, Component } from 'react';

type ErrorWithMessage = {
	message: string;
	source?: {
		errors?: {
			extensions?: {
				errorSource?: string;
			};
		}[];
	};
};

/**
 * List of error messages that should be handled by the relay error handler.
 * These errors are considered non-service faults and do not impact the Service Level Objective (SLO).
 */
const ALLOWED_RELAY_ERROR_MESSAGES = ['status code is: 429', 'status code is: 401'];
const ALLOWED_NETWORK_ERROR_MESSAGES = ['Failed to fetch'];
const NOT_ALLOWED_ERROR_SOURCES = ['GRAPHQL_GATEWAY'];

const isErrorWithMessage = (error: unknown): error is ErrorWithMessage =>
	typeof error === 'object' && error !== null && 'message' in error && Boolean(error.message);

/**
 * Determines whether a given error should be handled by the relay error handler.
 * This function checks if the error message contains any of the allowed messages.
 *
 * @param {unknown} error - The error object to be evaluated.
 * @returns {boolean} Returns true if the error is allowed to be handled, false otherwise.
 */
export const isRelayErrorAllowed = (error: ErrorWithMessage): boolean => {
	if (!isErrorWithMessage(error)) {
		return false;
	}

	const isErrorAllowed = ALLOWED_RELAY_ERROR_MESSAGES.some((message) =>
		error.message.includes(message),
	);

	const isMessageAllowed = ALLOWED_NETWORK_ERROR_MESSAGES.some((msg) =>
		error.message.includes(msg),
	);

	const isGraphqlGatewayError =
		error.source?.errors?.some((err) =>
			NOT_ALLOWED_ERROR_SOURCES.includes(err?.extensions?.errorSource || ''),
		) ?? false;

	return (isErrorAllowed || isMessageAllowed) && !isGraphqlGatewayError;
};

type State = {
	hasError: boolean;
};

interface CatchAndReportErrorsProps {
	children: ReactNode;
}

// This ErrorBoundary exists to catch expected Relay errorrs to stop them going to Sentry creating noise
export class CatchAndFilterExpectedRelayErrorsBoundary extends Component<
	CatchAndReportErrorsProps,
	State
> {
	static getDerivedStateFromError() {
		return { hasError: true };
	}

	state = {
		hasError: false,
	};

	componentDidCatch(error: Error) {
		if (isRelayErrorAllowed(error)) {
			return;
		}
		throw error;
	}

	render() {
		if (this.state.hasError) {
			return null;
		}
		return this.props.children;
	}
}
